import fieldSidebarSignal, {
	closeFieldSidebar,
} from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Signals/fieldSidebarSignal';
import FieldDraftFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/FieldDraftFormData';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import OptionsEditor from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sidebars/FieldSidebar/OptionsEditor';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useSelector } from '@verclam/synapse';
import { Button, Checkbox, Dropdown, Form, InputText, Sidebar } from '@verclam/ui-kit';

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isUsed: boolean;
};

/**
 * Edits one sheet field, new or existing, in a panel opened through `fieldSidebarSignal`. "Apply"
 * only updates the type draft; nothing is persisted before the page's "Enregistrer".
 */
const FieldSidebar: React.FC<Props> = ({ formData, onChange, isUsed }) => {
	const context = useSignalValue(fieldSidebarSignal);
	const natures = useSelector((state) => state.subjectTypeDefinitionsStore.fieldNatures);
	const formId = React.useId();

	const [fieldForm, setFieldForm] = React.useState(() =>
		FieldDraftFormData.createEmpty(isUsed, 'text', ''),
	);

	const editedIndex = context?.index ?? null;
	const editedField = editedIndex === null ? undefined : formData.fields[editedIndex];

	// The draft is re-seeded each time the panel opens, from the field it targets.
	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (editedField !== undefined) {
			setFieldForm(FieldDraftFormData.fromView(editedField, isUsed));
			return;
		}

		const firstNature = natures?.[0];
		setFieldForm(
			FieldDraftFormData.createEmpty(isUsed, firstNature?.value ?? 'text', firstNature?.label ?? ''),
		);
	}, [context, editedField, isUsed, natures]);

	const handleLabelChange = React.useCallback((value: string) => {
		setFieldForm((current) => current.setLabel(value));
	}, []);

	const handleNatureChange = React.useCallback(
		(value: string | null) => {
			const nature = natures?.find((entry) => entry.value === value);

			if (nature !== undefined) {
				setFieldForm((current) => current.setNature(nature.value, nature.label));
			}
		},
		[natures],
	);

	const handleRequiredChange = React.useCallback((checked: boolean) => {
		setFieldForm((current) => current.setRequired(checked));
	}, []);

	const handleSentToAiChange = React.useCallback((checked: boolean) => {
		setFieldForm((current) => current.setSentToAi(checked));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (context === null) {
			return;
		}

		if (!fieldForm.isFormDataValid()) {
			setFieldForm(fieldForm.showErrors());
			return;
		}

		const draft = fieldForm.toView();
		const index = context.index;

		onChange((current) =>
			index === null ? current.addField(draft) : current.replaceField(index, draft),
		);
		closeFieldSidebar();
	}, [context, fieldForm, onChange]);

	const isNew = context?.index === null;

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeFieldSidebar}
			position="right"
			header={isNew ? 'Nouveau champ' : 'Modifier le champ'}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeFieldSidebar}
					/>
					<Button type="submit" form={formId} label="Appliquer" />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={isNew ? 'Créer un champ' : 'Modifier un champ'}
			>
				<InputText
					name="fieldLabel"
					label="Libellé"
					required
					value={fieldForm.label}
					onChange={handleLabelChange}
					errorMessage={fieldForm.getValidationMessage('label')}
					isFormFieldValid={fieldForm.isValid('label')}
				/>
				<Dropdown
					name="fieldNature"
					label="Nature"
					required
					options={natures ?? []}
					optionLabel="label"
					optionValue="value"
					value={fieldForm.nature}
					disabled={fieldForm.isNatureLocked}
					isFormFieldValid
					subLabel={fieldForm.isNatureLocked ? '(figée : des dossiers existent)' : undefined}
					onChange={handleNatureChange}
				/>
				<OptionsEditor formData={fieldForm} onChange={setFieldForm} />
				<ConditionalLoader data={fieldForm.canBeRequired}>
					<Checkbox
						name="fieldRequired"
						label="Obligatoire"
						checked={fieldForm.required}
						onChange={handleRequiredChange}
					/>
				</ConditionalLoader>
				<Checkbox
					name="fieldSentToAi"
					label="Envoyé à l'IA"
					checked={fieldForm.sentToAi}
					onChange={handleSentToAiChange}
				/>
			</Form>
		</Sidebar>
	);
};

export default FieldSidebar;
